import type { PresentationBlock, QuoteDocument, QuoteFile } from 'paintscout'
import { getQuoteHomeImage } from '@paintscout/util/builder'
import { imageCrossOrigin } from '@paintscout/ui'
import { isPreviewQuote } from '../AboutEstimator/getSampleEstimator'

/**
 * Stands in for the estimate's home photo while a template is edited or previewed, where there is
 * no real estimate (#25097). Same file the PeakTone example uses as its "Yard Sign" image.
 */
export const HOME_IMAGE_SAMPLE: QuoteFile = {
  src: 'https://paintscout.s3.us-east-1.amazonaws.com/presentations/yard-sign.png',
  format: 'png',
  type: 'image/png',
  s3PublicKey: 'presentations/yard-sign',
  width: 1536,
  height: 1024,
  visibility: 'visible'
} as QuoteFile

export interface BannerImage {
  image: QuoteFile
  /** True when `image` comes from the estimate (or stands in for it), not from the block. */
  isHomeImage: boolean
}

export interface ImagePreloadRequest {
  src: string
  crossOrigin?: 'anonymous'
}

/**
 * The request FilePreview makes for `file`, so a preload fills the same cache entry: S3 files load
 * from `src` with `imageCrossOrigin` (a non-CORS load can poison Safari's shared cache entry, see
 * corsImage.ts), and other files with a bare `src` load it as is. Null for a Cloudinary file, whose
 * URL is only built when it renders.
 */
export function getImagePreloadRequest(file: QuoteFile | null | undefined): ImagePreloadRequest | null {
  if (!file?.src) {
    return null
  }
  if (file.s3PublicKey) {
    return { src: file.src, crossOrigin: imageCrossOrigin(file) }
  }
  return file.cloudinaryPublicId ? null : { src: file.src }
}

/** How long a banner waits on a decode before it swaps anyway, so a stalled load can't freeze it. */
export const DECODE_TIMEOUT_MS = 2000

/**
 * Loads and decodes the image off-screen. Resolves either way, at the latest after
 * `DECODE_TIMEOUT_MS`; a failed load is FilePreview's to show.
 */
export function decodeImage(request: ImagePreloadRequest): Promise<void> {
  const img = new Image()
  if (request.crossOrigin) {
    img.crossOrigin = request.crossOrigin
  }
  img.src = request.src
  if (!img.decode) {
    return Promise.resolve()
  }
  let timer: ReturnType<typeof setTimeout>
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, DECODE_TIMEOUT_MS)
  })
  return Promise.race([img.decode().catch(() => {}), timeout]).finally(() => clearTimeout(timer))
}

let homeImageSamplePreloaded = false

/**
 * Loads the sample photo before the author first ticks "Use home image from estimate", so the
 * first swap doesn't wait on a download (#25097).
 */
export function preloadHomeImageSample() {
  if (homeImageSamplePreloaded || typeof Image === 'undefined') {
    return
  }
  const request = getImagePreloadRequest(HOME_IMAGE_SAMPLE)
  if (request) {
    homeImageSamplePreloaded = true
    void decodeImage(request)
  }
}

/**
 * The image a Banner renders, in either layout. With "Use home image from estimate" on, that is
 * the estimate's home photo or first photo (`getQuoteHomeImage`), cropped from the top so a house
 * keeps its roofline. The block's own image is the fallback when the estimate has no photo.
 */
export function getBannerImage(args: {
  block: PresentationBlock
  quote?: QuoteDocument | null
  isSettings?: boolean
}): BannerImage {
  const { block, quote, isSettings } = args
  const fields = block?.fields ?? {}

  if (!fields.useHomeImage) {
    return { image: fields.image, isHomeImage: false }
  }

  const homeImage = isSettings || isPreviewQuote(quote) ? HOME_IMAGE_SAMPLE : getQuoteHomeImage({ quote })
  if (!homeImage) {
    return { image: fields.image, isHomeImage: false }
  }

  return {
    image: {
      ...homeImage,
      presentationOptions: { ...homeImage.presentationOptions, objectPositionX: 50, objectPositionY: 0 }
    },
    isHomeImage: true
  }
}
